// src/components/aiondemand/JobDetail.jsx

import { useEffect, useState } from 'react';

import {
    ArrowLeft, CheckCircle2, AlertCircle, Loader2, Clock,
    Eye, EyeOff, MapPin, Hash,
    Archive, ArchiveRestore, Trash2
} from 'lucide-react';

import { useAIOnDemandStore } from '../../stores/useAIOnDemandStore';
import { fetchWithAuth } from '../../api/fetchWithAuth';
import { getActionById } from '../../actions/registry.js';

export default function JobDetail({ jobId, mapRef, onBack }) {
    const jobs = useAIOnDemandStore((s) => s.jobs);
    const archivedJobs = useAIOnDemandStore((s) => s.archivedJobs);
    const jobLayerVisible = useAIOnDemandStore((s) => s.jobLayerVisible);
    const toggleJobLayer = useAIOnDemandStore((s) => s.toggleJobLayer);
    const archiveJob = useAIOnDemandStore((s) => s.archiveJob);
    const unarchiveJob = useAIOnDemandStore((s) => s.unarchiveJob);
    const deleteJob = useAIOnDemandStore((s) => s.deleteJob);

    const job = jobs.find((j) => j.id === jobId)
        ?? archivedJobs.find((j) => j.id === jobId);

    const [fullJob, setFullJob] = useState(null);

    const [pendingDelete, setPendingDelete] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        async function load() {
            try {
                const res = await fetchWithAuth(`/api/v1/jobs/${jobId}`);
                if (!res.ok) return;
                const data = await res.json();
                if (!cancelled) setFullJob(data);
            } catch (err) {
                console.error('[JobDetail] failed to fetch job', err);
            }
        }
        load();
        return () => { cancelled = true; };
    }, [jobId]);

    if (!job) {
        return (
            <div className="p-3 text-xs text-fg-secondary">
                Job not found in current list.
                <button onClick={onBack} className="ml-2 text-accent-info hover:underline">Back</button>
            </div>
        );
    }

    const action = getActionById(job.task_key);
    const isComplete = job.status === 'completed';
    const layerOn = !!jobLayerVisible[jobId];

    return (
        <div className="flex flex-col h-full overflow-hidden">
            <div className="px-3 pt-2 pb-1 shrink-0">
                <button onClick={onBack} className="flex items-center gap-1 text-[10px] text-fg-secondary hover:text-slate-300 transition">
                    <ArrowLeft size={11} /> Jobs
                </button>
            </div>

            <div className="px-3 pb-2 shrink-0">
                <div className="flex items-center gap-2">
                    <StatusIcon status={job.status} />
                    <h3 className="text-xs font-semibold text-slate-100">
                        {action?.name ?? job.task_key}
                    </h3>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-3">
                <DetailRow icon={<Hash size={10} />} label="Job ID">
                    <span className="font-mono text-[10px]">{job.id.slice(0, 8)}…</span>
                </DetailRow>

                <DetailRow icon={<Clock size={10} />} label="Started">
                    {job.started_at ? new Date(job.started_at).toLocaleString() : '—'}
                </DetailRow>

                {job.completed_at && (
                    <DetailRow icon={<Clock size={10} />} label="Completed">
                        {new Date(job.completed_at).toLocaleString()}
                    </DetailRow>
                )}

                {job.aoi_id && (
                    <DetailRow icon={<MapPin size={10} />} label="AOI">
                        <span className="font-mono text-[10px]">{job.aoi_id.slice(0, 8)}…</span>
                    </DetailRow>
                )}

                {job.processed_tile_count != null && (
                    <DetailRow label="Tiles processed">
                        {job.processed_tile_count}
                    </DetailRow>
                )}

                {job.processing_seconds != null && (
                    <DetailRow label="Processing time">
                        {Math.round(job.processing_seconds)}s
                    </DetailRow>
                )}

                {job.error_message && (
                    <div className="rounded-md border border-red-500/30 bg-red-500/5 p-2">
                        <div className="flex items-center gap-1.5 text-[11px] text-red-400 mb-1">
                            <AlertCircle size={11} /> Error
                        </div>
                        <p className="text-[10px] text-red-400/80 leading-relaxed">
                            {job.error_message}
                        </p>
                    </div>
                )}
            </div>

            {(isComplete || job.status === 'failed') && (
                <div className="border-t border-slate-800 shrink-0">
                    {/* Show on map — only for completed */}
                    {isComplete && (
                        <div className="px-3 py-2">
                            <button
                                onClick={() => toggleJobLayer(jobId)}
                                className={[
                                    'w-full flex items-center justify-center gap-1.5 px-3 py-1.5',
                                    'rounded-md border text-[11px] font-medium transition',
                                    layerOn
                                        ? 'border-status-ok/60 text-status-ok'
                                        : 'border-slate-700 bg-bg text-slate-300 hover:border-status-ok/40 hover:text-status-ok',
                                ].join(' ')}
                            >
                                {layerOn ? <><EyeOff size={11} /> Hide on map</> : <><Eye size={11} /> Show on map</>}
                            </button>
                        </div>
                    )}

                    {/* Archive / Delete row */}
                    <div className="px-3 py-2 border-t border-slate-800 flex items-center gap-2">
                        {job.archived_at ? (
                            <button
                                onClick={async () => {
                                    setBusy(true);
                                    await unarchiveJob(jobId);
                                    setBusy(false);
                                    onBack();
                                }}
                                disabled={busy}
                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5
                     rounded-md border border-slate-700 bg-bg text-slate-300
                     hover:border-accent-action/40 hover:text-accent-action text-[11px] transition
                     disabled:opacity-50"
                            >
                                <ArchiveRestore size={11} /> Unarchive
                            </button>
                        ) : (
                            <button
                                onClick={async () => {
                                    setBusy(true);
                                    await archiveJob(jobId);
                                    setBusy(false);
                                    onBack();
                                }}
                                disabled={busy}
                                className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5
                     rounded-md border border-slate-700 bg-bg text-slate-300
                     hover:border-slate-600 text-[11px] transition
                     disabled:opacity-50"
                            >
                                <Archive size={11} /> Archive
                            </button>
                        )}

                        {pendingDelete ? (
                            <>
                                <button
                                    onClick={async () => {
                                        setBusy(true);
                                        await deleteJob(jobId);
                                        setBusy(false);
                                        onBack();
                                    }}
                                    disabled={busy}
                                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5
                       rounded-md border border-red-500/40 bg-red-500/10 text-red-400
                       hover:bg-red-500/20 text-[11px] font-medium transition
                       disabled:opacity-50"
                                >
                                    <Trash2 size={11} /> Confirm delete
                                </button>
                                <button
                                    onClick={() => setPendingDelete(false)}
                                    disabled={busy}
                                    className="px-2 py-1.5 text-[11px] text-fg-secondary hover:text-slate-300 transition"
                                >
                                    Cancel
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={() => setPendingDelete(true)}
                                className="flex items-center justify-center gap-1 px-2 py-1.5
                     rounded-md border border-slate-700 bg-bg text-fg-secondary
                     hover:border-red-500/40 hover:text-red-400 text-[11px] transition"
                            >
                                <Trash2 size={11} />
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

function StatusIcon({ status }) {
    if (status === 'completed') return <CheckCircle2 size={13} className="text-status-busy" />;
    if (status === 'failed') return <AlertCircle size={13} className="text-red-400" />;
    if (status === 'running') return <Loader2 size={13} className="text-status-busy animate-spin" />;
    return <Clock size={13} className="text-fg-secondary" />;
}

function DetailRow({ icon, label, children }) {
    return (
        <div className="text-[11px]">
            <div className="flex items-center gap-1 text-fg-secondary mb-0.5">
                {icon}
                <span className="text-[9px] font-mono uppercase tracking-[0.14em]">{label}</span>
            </div>
            <div className="text-slate-200 pl-[14px]">{children}</div>
        </div>
    );
}